<template>
    <div class="messages-wrap">
        <div
            ref="scrollEl"
            class="messages"
            @scroll="onScroll"
        >
            <div class="messages-inner">
                <div v-if="showHistoryLoader" class="history-loader">
                    <ChatPreloader
                        label="Загружаем историю диалога…"
                        variant="spinner"
                    />
                </div>

                <template v-else-if="!showHistoryLoader">
                    <div v-if="!messages.length" class="empty">
                        <div class="empty-title">Спросите AI-аналитика</div>
                        <p class="empty-text">
                            Узнайте про динамику рынка, топ марок, регионы, прогноз 2026
                            или задайте свой вопрос. Ответ придёт в режиме реального времени
                            со ссылками на источники.
                        </p>
                        <div class="empty-chips">
                            <button
                                v-for="suggestion in suggestions"
                                :key="suggestion.id"
                                type="button"
                                class="empty-chip"
                                @click="$emit('suggest', suggestion.text)"
                            >
                                {{ suggestion.text }}
                            </button>
                        </div>
                    </div>

                    <ChatMessage
                        v-for="message in messages"
                        :key="message.id"
                        :message="message"
                        :user-initials="userInitials"
                        :user-name="userName"
                        :selection-mode="selectionMode"
                        :is-selected="selectedIds.includes(message.id)"
                        :is-streaming="isStreaming"
                        :active-message-id="activeMessageId"
                        @regenerate="$emit('regenerate', $event)"
                        @copy="$emit('copy', message)"
                        @feedback="$emit('feedback', message, $event)"
                        @followup="$emit('followup', message, $event)"
                        @toggle-select="$emit('toggle-select', $event)"
                        @update-card-view="$emit('update-card-view', $event)"
                        @edit="$emit('edit', $event)"
                        @version-prev="$emit('version-prev', $event)"
                        @version-next="$emit('version-next', $event)"
                    />
                </template>
            </div>
        </div>

        <button
            v-if="showJump"
            type="button"
            class="jump-button"
            @click="jumpToBottom"
        >
            <span>{{ unreadCount > 0 ? `Новые сообщения · ${unreadCount}` : 'К новым сообщениям' }}</span>
            <span class="jump-arrow">↓</span>
        </button>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ChatMessage from './ChatMessage.vue'
import ChatPreloader from '@/components/chat/ChatPreloader.vue'

const props = defineProps({
    messages: { type: Array, required: true },
    isLoadingHistory: { type: Boolean, default: false },
    userInitials: { type: String, default: 'ГМ' },
    userName: { type: String, default: '' },
    selectionMode: { type: Boolean, default: false },
    selectedIds: { type: Array, default: () => [] },
    isStreaming: { type: Boolean, default: false },
    activeMessageId: { type: String, default: null },
})

defineEmits(['regenerate', 'copy', 'feedback', 'followup', 'suggest', 'toggle-select'])

/** Проп иногда приходит как Ref — приводим к boolean. */
const showHistoryLoader = computed(() => {
    const v = props.isLoadingHistory
    if (typeof v === 'boolean') return v
    if (v && typeof v === 'object' && 'value' in v) return Boolean(v.value)
    return Boolean(v)
})

const scrollEl = ref(null)
const isAtBottom = ref(true)
const showJump = ref(false)
const unreadCount = ref(0)
const STICKY_THRESHOLD = 120

const suggestions = [
    { id: 's1', text: 'Сравни Haval vs Chery в Москве' },
    { id: 's2', text: 'Покажи динамику продаж новых легковых' },
    { id: 's3', text: 'Дай топ-10 марок в апреле' },
    { id: 's4', text: 'Как продажи распределены по регионам?' },
    { id: 's5', text: 'Сделай прогноз 2026 с тремя сценариями' },
]

function distanceFromBottom() {
    const el = scrollEl.value
    if (!el) return 0
    return el.scrollHeight - el.scrollTop - el.clientHeight
}

function onScroll() {
    const dist = distanceFromBottom()
    isAtBottom.value = dist < STICKY_THRESHOLD
    if (isAtBottom.value) {
        showJump.value = false
        unreadCount.value = 0
    }
}

function scrollToBottomNow() {
    if (!scrollEl.value) return
    scrollEl.value.scrollTop = scrollEl.value.scrollHeight
}

function jumpToBottom() {
    if (!scrollEl.value) return
    scrollEl.value.scrollTo({
        top: scrollEl.value.scrollHeight,
        behavior: 'smooth',
    })
    isAtBottom.value = true
    showJump.value = false
    unreadCount.value = 0
}

let resizeObserver = null

onMounted(() => {
    nextTick(scrollToBottomNow)
    if (typeof ResizeObserver !== 'undefined' && scrollEl.value) {
        resizeObserver = new ResizeObserver(() => {
            if (isAtBottom.value) {
                scrollToBottomNow()
            }
        })
        resizeObserver.observe(scrollEl.value)
    }
})

onBeforeUnmount(() => {
    if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
    }
})

watch(
    () => props.messages.length,
    async (next, prev) => {
        await nextTick()
        if (isAtBottom.value) {
            scrollToBottomNow()
        } else if (next > (prev || 0)) {
            unreadCount.value += next - (prev || 0)
            showJump.value = true
        }
    }
)

watch(
    () => props.messages.map(m => {
        const textLen = m.text ? m.text.length : 0
        const cards = Array.isArray(m.cards) ? m.cards.length : 0
        const lists = Array.isArray(m.lists) ? m.lists.length : 0
        const callouts = Array.isArray(m.callouts) ? m.callouts.length : 0
        const followups = m.followups ? 1 : 0
        return `${textLen}-${cards}-${lists}-${callouts}-${followups}-${m.status}`
    }).join('|'),
    async () => {
        await nextTick()
        if (!scrollEl.value) return
        if (isAtBottom.value) {
            scrollToBottomNow()
        } else {
            showJump.value = true
        }
    }
)
</script>

<style scoped>
.messages-wrap {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    background: white;
}

.messages {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 24px 20px;
    background: white;
    scroll-behavior: smooth;
}

.messages-inner {
    max-width: 760px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.history-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: min(420px, 55vh);
}

.history-loader :deep(.preloader) {
    min-height: auto;
    padding: 0;
}

.history-loader :deep(.label) {
    color: #23282C;
    font-weight: 500;
}

.jump-button {
    position: absolute;
    left: 50%;
    bottom: 14px;
    transform: translateX(-50%);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    border-radius: 18px;
    border: 1px solid #E8E6E0;
    background: white;
    color: #23282C;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(21, 26, 34, 0.15);
    z-index: 4;
}

.jump-button:hover {
    border-color: #23282C;
}

.jump-arrow {
    font-weight: 700;
}

.empty {
    max-width: 640px;
    margin: 24px auto;
    text-align: center;
    color: #23282C;
}

.empty-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
}

.empty-text {
    font-size: 13px;
    line-height: 1.6;
    color: #5F5E5A;
    margin: 0 auto 18px;
    max-width: 480px;
}

.empty-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
}

.empty-chip {
    font-family: inherit;
    font-size: 12px;
    padding: 8px 14px;
    border-radius: 18px;
    border: 1px solid #E8E6E0;
    background: white;
    color: #23282C;
    cursor: pointer;
    transition: border-color 0.15s ease;
}

.empty-chip:hover {
    border-color: #23282C;
}

@media (max-width: 768px) {
    .messages {
        padding: 16px 16px 20px;
        gap: 18px;
    }
}
</style>
